import { useEffect } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { t } from '@/lib/i18n';

interface RouteErrorProps {
    error: Error;
    /** Provided by TanStack Router — re-runs the route's loader/component. */
    reset?: () => void;
}

/**
 * Route-scoped error fallback. Rendered by the router's defaultErrorComponent so a
 * thrown page error is contained to the page area — the app shell (sidebar/top-nav)
 * stays usable instead of the whole dashboard going blank.
 *
 * Also covers lazy-chunk load failures (common right after a new deploy): those
 * need a full reload to fetch the new asset, so we offer a "reload" action.
 */
export function RouteError({ error, reset }: RouteErrorProps) {
    useEffect(() => {
        // Surface for diagnostics; never render raw error details to the user.
        console.error('[route-error]', error);
    }, [error]);

    // Stale/needs-new-asset signatures from a deploy → a soft retry won't help.
    const isChunkLoadError = /loading chunk|dynamically imported module|importing a module script failed/i.test(
        error?.message ?? '',
    );

    return (
        <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div className="space-y-1">
                <h2 className="text-lg font-semibold">{t('errors.routeError.title')}</h2>
                <p className="max-w-md text-sm text-muted-foreground">
                    {t('errors.routeError.description')}
                </p>
            </div>
            <div className="flex items-center gap-2">
                {isChunkLoadError || !reset ? (
                    <Button onClick={() => window.location.reload()}>
                        <RotateCw className="h-4 w-4 me-1.5" />
                        {t('errors.routeError.reload')}
                    </Button>
                ) : (
                    <Button onClick={reset}>
                        <RotateCw className="h-4 w-4 me-1.5" />
                        {t('errors.routeError.retry')}
                    </Button>
                )}
            </div>
        </div>
    );
}
